<template>
  <div class="scorm-player-container position-relative">

    <!-- Resume Prompt Overlay -->
    <div v-if="showResumePrompt" class="resume-prompt-overlay">
      <div class="card p-4 text-center shadow-lg" style="max-width: 400px;">
        <h4 class="text-dark mb-3">Resume Training?</h4>
        <p class="text-muted mb-4">We detected previous progress for this module. Would you like to pick up where you left off, or start a completely new attempt?</p>
        <div class="d-flex justify-content-center gap-3">
          <button class="btn btn-outline-secondary px-4" @click="handleResumeChoice(false)">Start Over</button>
          <button class="btn bg-theme text-white px-4" @click="handleResumeChoice(true)">Resume</button>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-else-if="!scormContentUrl" class="loading-state">
      <div class="spinner-border text-info" role="status"></div>
      <p class="mt-2 text-white">Initializing SCORM Module...</p>
    </div>

    <!-- Active Player Frame -->
    <template v-else>
      <!-- Completion Floating Banner / Action Bar -->
      <transition name="fade">
        <div v-if="isCompleted" class="completion-bar">
          <div class="d-flex align-items-center justify-content-between w-100 px-3 py-2">
            <div class="d-flex align-items-center gap-2 text-white">
              <i class="fas fa-check-circle text-success fs-5"></i>
              <span class="fw-semibold">Module Completed!</span>
              <span v-if="countdownSeconds !== null && countdownSeconds > 0" class="text-white-50 ms-2 small">
                Redirecting in {{ countdownSeconds }}s...
              </span>
            </div>
            <div class="d-flex align-items-center gap-2">
              <button
                v-if="countdownSeconds !== null && countdownSeconds > 0"
                class="btn btn-sm btn-outline-light px-2"
                @click="cancelAutoRedirect"
                title="Pause auto-redirect to review the slide"
              >
                Stay on Slide
              </button>
              <button
                class="btn btn-sm bg-theme text-white px-3 fw-bold shadow-sm"
                @click="completeAndExit"
              >
                <i class="fas fa-arrow-right me-1"></i> Return to Completed Training
              </button>
            </div>
          </div>
        </div>
      </transition>

      <iframe
        ref="scormIframe"
        :src="scormContentUrl"
        class="scorm-iframe"
        frameborder="0"
        allow="autoplay; fullscreen"
        @load="handleIframeLoad"
      ></iframe>
    </template>
  </div>
</template>

<script>
export default {
  name: 'ScormPlayer',
  props: {
    assignmentId: { type: [Number, String], required: true },
    moduleId: { type: [Number, String], required: true },
    userId: { type: [Number, String], required: true },
    launchPath: { type: String, required: true }
  },

  data() {
    return {
      scormContentUrl: '',
      cmiData: {},
      lmsInitialized: false,
      alreadyMarkedComplete: false,
      showResumePrompt: false,
      tempDbTrackingData: null,
      // Completion & Redirection State
      isCompleted: false,
      hasEmittedCompletion: false,
      countdownSeconds: null,
      countdownInterval: null
    };
  },

  mounted() {
    window.addEventListener('message', this.handleWindowMessage);
    this.initPlayer();
  },

  unmounted() {
    this.cancelAutoRedirect();
    window.removeEventListener('message', this.handleWindowMessage);
    // Clean up global window hooks so they don't leak b0etween routes!
    delete window.API;
    delete window.API_1484_11;
    delete window.DoCPExit;
  },

  watch: {
    assignmentId() {
      this.cancelAutoRedirect();
      this.isCompleted = false;
      this.hasEmittedCompletion = false;
      this.alreadyMarkedComplete = false;
      this.initPlayer();
    },
    moduleId() {
      this.cancelAutoRedirect();
      this.isCompleted = false;
      this.hasEmittedCompletion = false;
      this.alreadyMarkedComplete = false;
      this.initPlayer();
    },
    launchPath() {
      this.cancelAutoRedirect();
      this.isCompleted = false;
      this.hasEmittedCompletion = false;
      this.alreadyMarkedComplete = false;
      this.initPlayer();
    }
  },

  methods: {
    async initPlayer() {
      if (this.assignmentId !== 'preview') {
        await this.loadScormTrackingData();
      } else {
        this.launchScormIframe(); // Skip tracking for previews
      }
    },

    handleWindowMessage(event) {
      try {
        let data = event.data;
        if (typeof data === 'string') {
          try {
            data = JSON.parse(data);
          } catch (e) {
            void e;
          }
        }
        if (!data) return;

        // 1. Fair Work Commission / Captivate web object postMessage
        if (data.func === 'fwc_scorm_api') {
          if (data.message === 'GOTO_DASHBOARD' || data.message === 'GOTO_EXIT') {
            this.triggerCompletion(true);
            return;
          }
        }

        // 2. Adobe Captivate internal exit event
        if (data.name === 'WIN8APPCLOSE' || data.name === 'CPMovieExit' || data === 'WIN8APPCLOSE') {
          this.triggerCompletion(true);
          return;
        }

        // 3. Generic SCORM package exit / finish / complete events
        if (
          data.action === 'exit' || data.action === 'finish' || data.action === 'complete' ||
          data.type === 'SCORM_EXIT' || data.type === 'SCORM_FINISH' || data.type === 'SCORM_COMPLETED' ||
          data === 'SCORM_EXIT' || data === 'SCORM_FINISH' || data === 'SCORM_COMPLETED'
        ) {
          this.triggerCompletion(true);
          return;
        }
      } catch (err) {
        console.warn('Error handling postMessage from SCORM:', err);
      }
    },

    handleIframeLoad() {
      try {
        const iframe = this.$refs.scormIframe;
        if (iframe && iframe.contentWindow) {
          const href = iframe.contentWindow.location.href || '';
          if (href.includes('goodbye.html') || href.includes('goodbye')) {
            this.triggerCompletion(true);
          }
        }
      } catch (e) {
        // Cross-origin access may fail if navigated outside domain, ignore
        void e;
      }
    },

    async triggerCompletion(immediateExit = false) {
      this.isCompleted = true;

      const curStatus = (
        this.cmiData['cmi.core.lesson_status'] ||
        this.cmiData['cmi.completion_status'] ||
        ''
      ).toLowerCase();

      if (curStatus !== 'passed' && curStatus !== 'completed') {
        this.cmiData['cmi.core.lesson_status'] = 'completed';
        this.cmiData['cmi.completion_status'] = 'completed';
      }

      if (this.assignmentId !== 'preview') {
        await this.markCompletedSilently();
        await this.saveScormTrackingData(this.cmiData);
      }

      if (immediateExit) {
        this.completeAndExit();
        return;
      }

      // If auto-redirect is not active, start countdown (5s)
      if (this.countdownSeconds === null && !this.hasEmittedCompletion) {
        this.startAutoRedirect(5);
      }
    },

    startAutoRedirect(seconds = 5) {
      this.cancelAutoRedirect();
      this.countdownSeconds = seconds;
      this.countdownInterval = setInterval(() => {
        if (this.countdownSeconds > 1) {
          this.countdownSeconds -= 1;
        } else {
          this.cancelAutoRedirect();
          this.completeAndExit();
        }
      }, 1000);
    },

    cancelAutoRedirect() {
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
        this.countdownInterval = null;
      }
      this.countdownSeconds = null;
    },

    async completeAndExit() {
      if (this.hasEmittedCompletion) return;
      this.hasEmittedCompletion = true;
      this.cancelAutoRedirect();

      if (this.assignmentId !== 'preview') {
        await this.markCompletedSilently();
        await this.saveScormTrackingData(this.cmiData);
      }

      this.$emit('scormCompleted');
    },

    async markCompletedSilently() {
      if (this.assignmentId === 'preview') return;

      // Prevent spamming the API if the SCORM package updates status multiple times
      if (this.alreadyMarkedComplete) return;
      this.alreadyMarkedComplete = true;

      try {
        await this.$axios.put(`/trainings/users/${this.userId}/assignments/${this.assignmentId}/complete`);
      } catch (error) {
        console.warn('Silent fail: Could not mark SCORM as complete', error);
      }
    },

    async loadScormTrackingData() {
      try {
        const response = await this.$axios.get(`/trainings/users/${this.userId}/assignments/${this.assignmentId}/scorm-tracking`);

        if (response.data && (response.data.id || response.data.ID)) {
          const loc = response.data.cmi_location || '';
          const sus = response.data.cmi_suspend_data || '';

          // TRAP: If they have a bookmark AND they are in Rewatch mode, pause and ask!
          if ((loc !== '' || sus !== '') && this.$route.query.rewatch) {
             this.tempDbTrackingData = response.data;
             this.showResumePrompt = true;
             return; // Stop here and wait for user input
          }

          // Otherwise, auto-resume normally
          this.applyTrackingToState(loc, sus, response.data.score, response.data.status);
        }

        // Launch if no prompt was needed
        this.launchScormIframe();
      } catch (error) {
        console.error('Error loading SCORM tracking data:', error);
        this.launchScormIframe(); // Fail-safe: launch anyway
      }
    },

    async handleResumeChoice(wantsToResume) {
      this.showResumePrompt = false;

      if (wantsToResume) {
        this.applyTrackingToState(
          this.tempDbTrackingData.cmi_location,
          this.tempDbTrackingData.cmi_suspend_data,
          this.tempDbTrackingData.score,
          this.tempDbTrackingData.status
        );
      } else {
        // START OVER completely wipes the local state
        this.applyTrackingToState('', '', '', 'incomplete');
        await this.saveScormTrackingData(this.cmiData);
      }

      this.launchScormIframe();
    },

    applyTrackingToState(loc, sus, score, status) {
        loc = loc || '';
        sus = sus || '';
        const safeStatus = status || 'incomplete';

        let scormScore = score || '';
        if (this.$route.query.rewatch) {
           scormScore = '';
        }

        this.cmiData['cmi.core.lesson_location'] = loc;
        this.cmiData['cmi.location'] = loc;
        this.cmiData['cmi.suspend_data'] = sus;

        this.cmiData['cmi.core.score.raw'] = scormScore;
        this.cmiData['cmi.score.raw'] = scormScore;

        this.cmiData['cmi.core.lesson_status'] = safeStatus;
        this.cmiData['cmi.completion_status'] = safeStatus;

        if (loc !== '' || sus !== '') {
          this.cmiData['cmi.core.entry'] = 'resume';
          this.cmiData['cmi.entry'] = 'resume';
        } else {
          this.cmiData['cmi.core.entry'] = 'ab-initio';
          this.cmiData['cmi.entry'] = 'ab-initio';
        }
    },

    launchScormIframe() {
      this.setupGlobalScormAPI();
      const delimiter = this.launchPath && this.launchPath.includes('?') ? '&' : '?';
      this.scormContentUrl = `/scorm_iframe/trainings/users/${this.userId}/modules/${this.moduleId}/scorm-content/${this.launchPath}${delimiter}_t=${Date.now()}`;
    },

    async saveScormTrackingData(cmiData) {
      if (this.assignmentId === 'preview') return;
      try {
        const currentStatus = (
          cmiData['cmi.core.lesson_status'] ||
          cmiData['cmi.success_status'] ||
          cmiData['cmi.completion_status'] ||
          'incomplete'
        ).toLowerCase();

        const payload = {
          cmi_location: cmiData['cmi.core.lesson_location'] || cmiData['cmi.location'] || '',
          cmi_suspend_data: cmiData['cmi.suspend_data'] || '',
          score: parseFloat(cmiData['cmi.core.score.raw'] || cmiData['cmi.score.raw'] || 0),
          status: currentStatus
        };

        await this.$axios.post(`/trainings/users/${this.userId}/assignments/${this.assignmentId}/scorm-tracking`, payload);
      } catch (error) {
        console.error('Error saving SCORM data:', error);
      }
    },

    async markInProgress() {
      if (this.assignmentId === 'preview') return;

      try {
        await this.$axios.put(`/trainings/users/${this.userId}/assignments/${this.assignmentId}/status`, {
          status: 'in_progress'
        });
      } catch (error) {
        console.warn('Silent fail: Could not mark SCORM as in progress', error);
      }
    },

    setupGlobalScormAPI() {
      const self = this;
      let saveTimeout = null;

      // Expose DoCPExit on window for Adobe Captivate exit integration
      window.DoCPExit = () => {
        self.triggerCompletion(true);
      };

      const initFunction = () => {
        self.lmsInitialized = true;
        self.markInProgress();
        return "true";
      };

      const finishFunction = () => {
        self.lmsInitialized = false;

        self.saveScormTrackingData(self.cmiData).then(() => {
          const curStatus = (
            self.cmiData['cmi.core.lesson_status'] ||
            self.cmiData['cmi.completion_status'] ||
            self.cmiData['cmi.success_status'] ||
            ''
          ).toLowerCase();
          const prog = parseFloat(self.cmiData['cmi.progress_measure'] || 0);

          if (curStatus === 'completed' || curStatus === 'passed' || self.isCompleted || prog >= 1.0) {
            self.completeAndExit();
          }
        });

        return "true";
      };

      const getValue = (param) => {
        return self.cmiData[param] !== undefined ? String(self.cmiData[param]) : "";
      };

      const setValue = (param, value) => {
        // 1. MAX SCORE TRACKING (Handles both 1.2 and 2004)
        if (param === 'cmi.core.score.raw' || param === 'cmi.score.raw' || param === 'cmi.score.scaled') {
          const oldScore = parseFloat(self.cmiData[param]) || 0;
          const newScore = parseFloat(value) || 0;

          if (newScore < oldScore) {
             return "true"; // Silently reject the lower score! Keep the higher one.
          }

          // SCORM 2004 Scaled Score (0.0 to 1.0) Fix
          if (param === 'cmi.score.scaled') {
            const translatedRaw = newScore * 100;
            self.cmiData['cmi.score.raw'] = translatedRaw;
            self.cmiData['cmi.core.score.raw'] = translatedRaw;
          }
          self.cmiData[param] = value;
          return "true";
        }

        // 2. STATUS DOWNGRADE TRAP
        if (param === 'cmi.core.lesson_status' || param === 'cmi.completion_status' || param === 'cmi.success_status') {
          const oldStatus = (self.cmiData[param] || '').toLowerCase();
          const valLower = String(value).toLowerCase();

          // If they already passed/completed it, ignore any attempts to make it incomplete/failed
          if ((oldStatus === 'completed' || oldStatus === 'passed') &&
              (valLower === 'incomplete' || valLower === 'failed' || valLower === 'browsed' || valLower === 'not attempted')) {
             return "true";
          }
        }

        // 3. Save the value to state (if it passed the traps)
        self.cmiData[param] = value;

        const valLower = String(value).toLowerCase();

        // 4. SCORM 2004 Progress Measure Trap (100% course progress)
        if (param === 'cmi.progress_measure') {
          const prog = parseFloat(value) || 0;
          if (prog >= 1.0) {
            self.cmiData['cmi.completion_status'] = 'completed';
            self.cmiData['cmi.core.lesson_status'] = 'completed';
            self.triggerCompletion();
          }
        }

        // 5. SCORM 2004 Completion Status Trap
        if (param === 'cmi.completion_status') {
          if (valLower === 'completed') {
            self.triggerCompletion();
          }
        }

        // 6. SCORM 1.2 Lesson Status Trap
        if (param === 'cmi.core.lesson_status') {
          if (valLower === 'completed') {
            self.triggerCompletion();
          } else if (valLower === 'passed') {
            // Guard against SCORM 2004 or multi-section packages reporting intermediate test pass while incomplete
            const prog = parseFloat(self.cmiData['cmi.progress_measure'] || 0);
            const compStatus = (self.cmiData['cmi.completion_status'] || '').toLowerCase();

            // If progress is tracked and not yet 100%, or completion_status is explicitly incomplete, don't trigger completion yet
            if (compStatus === 'incomplete' || (prog > 0 && prog < 1.0)) {
              // Wait for 100% course completion
            } else {
              self.triggerCompletion();
            }
          } else if (valLower === 'failed') {
            self.cmiData['cmi.core.lesson_location'] = '';
            self.cmiData['cmi.location'] = '';
            self.cmiData['cmi.suspend_data'] = '';
          }
        }

        // 7. Known End Location Trap (legacy packages without proper LMSFinish / completion reporting)
        if (param === 'cmi.core.lesson_location' || param === 'cmi.location') {
          const locLower = valLower;
          const isFinishLocation =
            locLower === 'finish' ||
            locLower === 'end' ||
            locLower === 'complete' ||
            locLower.includes('slide122765') ||
            locLower.includes('slide386005') ||
            locLower.includes('sh_s39');

          if (isFinishLocation) {
            const curStatus = (self.cmiData['cmi.core.lesson_status'] || self.cmiData['cmi.completion_status'] || '').toLowerCase();
            if (curStatus !== 'failed') {
              self.triggerCompletion();
            }
          }
        }

        // 8. Debounced Auto-Save
        if (param === 'cmi.core.lesson_location' || param === 'cmi.location' || param === 'cmi.suspend_data' || param.includes('score') || param.includes('status') || param === 'cmi.progress_measure') {
          clearTimeout(saveTimeout);
          saveTimeout = setTimeout(() => {
            self.saveScormTrackingData(self.cmiData);
          }, 1000);
        }

        return "true";
      };

      const commitFunction = () => {
        self.saveScormTrackingData(self.cmiData);
        return "true";
      };

      window.API = {
        LMSInitialize: initFunction,
        LMSFinish: finishFunction,
        LMSGetValue: getValue,
        LMSSetValue: setValue,
        LMSCommit: commitFunction,
        LMSGetLastError: () => "0",
        LMSGetErrorString: () => "No error",
        LMSGetDiagnostic: () => "No diagnostic information"
      };

      window.API_1484_11 = {
        Initialize: initFunction,
        Terminate: finishFunction,
        GetValue: getValue,
        SetValue: setValue,
        Commit: commitFunction,
        GetLastError: () => "0",
        GetErrorString: () => "No error",
        GetDiagnostic: () => "No diagnostic information"
      };
    }
  }
};
</script>

<style scoped>
.scorm-player-container {
  width: 100%;
  height: 100%;
  min-height: 550px;
  background-color: #0f172a;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
}

.scorm-iframe {
  width: 100%;
  height: 100%;
  min-height: 550px;
  background-color: #ffffff;
  border: none;
  flex-grow: 1;
}

.loading-state {
  text-align: center;
}

.resume-prompt-overlay {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background-color: rgba(15, 23, 42, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

.completion-bar {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  background: rgba(15, 23, 42, 0.92);
  backdrop-filter: blur(8px);
  border-bottom: 2px solid #00a0c1;
  z-index: 20;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.bg-theme {
  background-color: #00a0c1 !important;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>